
import { Shield, FileCheck, BarChart3, AlertTriangle, Lock, Scale } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const HipaaReadinessAssessment = () => {
  return (
    <LandingPageTemplate
      title="HIPAA Readiness Assessment"
      description="Comprehensive HIPAA compliance assessment to evaluate and enhance your healthcare organization's security posture."
      keywords={[
        "HIPAA assessment",
        "healthcare security",
        "regulatory compliance",
        "security evaluation",
        "healthcare privacy"
      ]}
      canonicalUrl="/cybersecurity/hipaa-readiness-assessment/"
      schemaType="service"
      schemaData={{
        name: "HIPAA Readiness Assessment Services",
        description: "Expert evaluation of your security program against HIPAA requirements",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Healthcare Privacy"
        highlighted="HIPAA Assessment"
        description="Expert evaluation of your healthcare organization's security controls and privacy measures against HIPAA requirements."
        ctaText="Schedule Assessment"
        heroPattern="roadmap"
      />

      <FeatureSection 
        title="Comprehensive HIPAA Assessment"
        subtitle="KEY FEATURES"
        description="Our HIPAA assessment provides a thorough evaluation of your security and privacy controls across all critical domains."
        features={[
          {
            title: "Privacy Controls",
            description: "Evaluate your privacy controls and data protection measures against HIPAA requirements.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Analysis",
            description: "Identify gaps between your current security posture and HIPAA requirements.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Review",
            description: "Comprehensive review of policies, procedures, and regulatory documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Assessment",
            description: "Thorough risk assessment based on HIPAA Security Rule guidelines.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Compliance Mapping",
            description: "Map your controls to HIPAA Privacy and Security Rules.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Controls",
            description: "Assessment of access controls for PHI and critical systems.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Assessment Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to evaluating your healthcare organization against HIPAA requirements"
        steps={[
          {
            title: "Initial Scoping",
            description: "Define assessment boundaries and identify PHI systems"
          },
          {
            title: "Control Review",
            description: "Assess security and privacy controls against HIPAA requirements"
          },
          {
            title: "Gap Analysis",
            description: "Identify compliance gaps and security weaknesses"
          },
          {
            title: "Risk Assessment",
            description: "Evaluate risks to PHI and prioritize remediation efforts"
          },
          {
            title: "Recommendations",
            description: "Develop detailed recommendations and compliance roadmap"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our HIPAA Assessment"
        benefits={[
          {
            title: "Healthcare Expertise",
            description: "Work with certified healthcare security specialists"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full evaluation across all HIPAA requirements"
          },
          {
            title: "Clear Action Plan",
            description: "Actionable recommendations prioritized by risk"
          },
          {
            title: "Regulatory Alignment",
            description: "Ensure compliance with HIPAA regulations"
          },
          {
            title: "Risk Reduction",
            description: "Identify and address security vulnerabilities"
          },
          {
            title: "Future Readiness",
            description: "Prepare for evolving healthcare regulations"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Assess Your HIPAA Compliance?"
        description="Schedule your HIPAA readiness assessment today and ensure your organization's regulatory compliance."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request HIPAA Assessment"
        modalDescription="Tell us about your organization's needs for HIPAA compliance assessment."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "HIPAA Assessment",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default HipaaReadinessAssessment;
